import { FormElement } from '../services/api';
import { checkValueExists } from '../services/valueExists';

/**
 * Custom JS validation is disabled for security (no new Function / eval).
 * Always fails closed so fields with custom rules do not silently pass.
 */
function runCustomValidation(_functionCode: unknown, _value: string): boolean {
  return false;
}

/** Synchronous validations only (no database lookups). */
export function validateFieldSync(element: FormElement, value: string): string[] {
  const errors: string[] = [];
  if (!element.validations || element.validations.length === 0) {
    return errors;
  }

  element.validations.forEach((validation) => {
    switch (validation.type) {
      case 'required': {
        if (!value || value.trim() === '') {
          errors.push(validation.message || `${element.label || element.name || 'Field'} is required`);
        }
        break;
      }
      case 'min_length': {
        const minLength = validation.config?.minLength || validation.config?.min || 0;
        if (value && value.length < minLength) {
          errors.push(validation.message || `Minimum length is ${minLength} characters`);
        }
        break;
      }
      case 'max_length': {
        const maxLength = validation.config?.maxLength || validation.config?.max || Infinity;
        if (value && value.length > maxLength) {
          errors.push(validation.message || `Maximum length is ${maxLength} characters`);
        }
        break;
      }
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          errors.push(validation.message || 'Please enter a valid email address');
        }
        break;
      }
      case 'url': {
        try {
          new URL(value);
        } catch {
          if (value) {
            errors.push(validation.message || 'Please enter a valid URL');
          }
        }
        break;
      }
      case 'pattern': {
        if (validation.config?.pattern) {
          const regex = new RegExp(validation.config.pattern);
          if (value && !regex.test(value)) {
            errors.push(validation.message || 'Invalid format');
          }
        }
        break;
      }
      case 'min': {
        const min = validation.config?.min;
        if (min !== undefined && value && parseFloat(value) < min) {
          errors.push(validation.message || `Minimum value is ${min}`);
        }
        break;
      }
      case 'max': {
        const max = validation.config?.max;
        if (max !== undefined && value && parseFloat(value) > max) {
          errors.push(validation.message || `Maximum value is ${max}`);
        }
        break;
      }
      case 'custom': {
        const isValid = runCustomValidation(validation.config?.functionCode, value);
        if (!isValid) {
          errors.push(validation.message || 'Invalid value');
        }
        break;
      }
      case 'db_value_exists':
        break;
    }
  });

  return errors;
}

export function fieldHasDbValidation(element: FormElement): boolean {
  return element.validations?.some((v) => v.type === 'db_value_exists') ?? false;
}

export type ValidateFieldOptions = {
  /** Hashed transaction id (unused for value-exists; kept for call-site compatibility). */
  hashedId?: string | null;
  /** Fired after db_value_exists lookup (for payment page analytics). */
  onDbValueExistsCheck?: (
    exists: boolean,
    column: string,
    value: string,
    currency: string,
  ) => void;
};

/** Runs sync validations plus async database lookups (HTTP `/transactions/value-exists`). */
export async function validateField(
  element: FormElement,
  value: string,
  options?: ValidateFieldOptions,
): Promise<string[]> {
  const errors = validateFieldSync(element, value);
  if (!element.validations || element.validations.length === 0) {
    return errors;
  }

  for (const validation of element.validations) {
    if (validation.type !== 'db_value_exists') continue;
    const column = validation.config?.column;
    if (!column || !value?.trim()) continue;

    const whenFound = validation.config?.whenFound === 'require' ? 'require' : 'reject';
    const currency =
      typeof validation.config?.currency === 'string' && validation.config.currency.trim()
        ? validation.config.currency.trim()
        : 'VES';
    try {
      const { exists } = await checkValueExists({
        column,
        value: value.trim(),
        currency,
        hashedId: options?.hashedId,
      });
      options?.onDbValueExistsCheck?.(exists, column, value.trim(), currency);
      const shouldFail = whenFound === 'reject' ? exists : !exists;
      if (shouldFail) {
        errors.push(
          validation.message ||
            (whenFound === 'reject'
              ? 'This value already exists'
              : 'This value was not found'),
        );
      }
    } catch {
      errors.push(validation.message || 'Unable to verify value');
    }
  }

  return errors;
}
